import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import FormatResourceStatus from "../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { SubscriptionInvoiceApi } from "../../../../../../../services/Api/Subscription/subscriptionTypes";
import { OrganizationApi } from "../../../../../../../services/Api/types";
import { downloadFile } from "../../../../../../../utils";
import { OrganizationPreviewRequests } from "../../services/types";

interface Props {
	organization: OrganizationApi;
	requests: OrganizationPreviewRequests;
}

const InvoicesTabPreview = ({ organization, requests }: Props) => {
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState<boolean>(false);
	const [invoices, setInvoices] = useState<SubscriptionInvoiceApi[]>([]);
	const { addFlash, addSuccessFlash } = useFlash();
	useEffect(() => {
		fetchInvoices();
	}, []);

	const exportProformaToPdf = async (id: string) => {
		const requestsExportProformaInvoicePdf = requests.exportProformaInvoicePdf;
		if (!requestsExportProformaInvoicePdf) return;
		await downloadFile(
			() => requestsExportProformaInvoicePdf(organization.id, id),
			`${t("common.word.proforma")}_${id}.pdf`,
			addFlash
		);
	};

	const fetchInvoices = async () => {
		if (!requests.invoices) return;
		setLoading(true);
		try {
			const res = await requests.invoices(organization.id);
			setInvoices(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	if (!requests.invoices) return null;
	if (loading) return <LoadingContainer />;

	return (
		<div className={"container-fluid"}>
			{!!(organization.payment_date_due || organization.not_paid_invoices_count) && (
				<div className={"mb-4 alert alert-warning"}>
					Klient ma <strong>{organization.not_paid_invoices_count} nieopłaconą faktur</strong>. Termin
					płatności jest <strong>{FormatDate(organization.payment_date_due)}</strong>. <br />
					Możesz wysłać link klientowi w celu opłacenia faktur:
					<div className={"mt-2 mb-2"}>
						<strong>https://accounts.gopos.io/{organization.id}/subscriptions</strong>
					</div>
					* Klient ma możliwość zapłaty za faktury za pomocą karty, e-transferu, blik. Rekomendowana formy
					płatności do podpięcie karty płatniczej.
				</div>
			)}
			<div className="table-responsive">
				<table className="table table-preview ">
					<thead>
						<tr>
							<th>Numer</th>
							<th>Data wystawienia</th>
							<th>Data faktury</th>
							<th>Data Status</th>
							<th>Kwota</th>
						</tr>
					</thead>
					<tbody>
						{invoices.map((item) => {
							return (
								<tr key={item.invoice_id}>
									<td>
										<Link
											to="#"
											onClick={() => {
												exportProformaToPdf(item.proforma_id);
											}}
										>
											{item.invoice_id ? (
												<>
													{item.invoice_number}
													<br />
													<small>{item.proforma_number}</small>
												</>
											) : (
												item.proforma_number
											)}
										</Link>
									</td>
									<td>{FormatDate(item?.proforma_created_at)}</td>
									<td>{FormatDate(item?.paid_at)}</td>
									<td>
										<div>
											<FormatResourceStatus
												status={
													item.payment_status === "OPEN" ? "NOT_PAID" : item.payment_status
												}
											/>
											<div>
												{item.last_paid_at &&
													t("modules.invoice.field.last_paid_at.title") +
														FormatDate(item.last_paid_at)}
											</div>
											<div>
												{item.payment_error &&
													t("modules.invoice.field.reason.title") + item.payment_error}
											</div>
										</div>
									</td>
									<td>
										<div className="d-flex flex-column">
											<span>
												{FormatMoney(item?.total_without_credits_and_adjustments_money)}
											</span>
											<small className="text-muted">
												Zapłacono:
												{FormatMoney(item?.paid_money)}
											</small>
											<small className="text-muted">
												Do zapłaty:
												{FormatMoney(item?.due_money)}
											</small>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default InvoicesTabPreview;
